/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { parseError } from '../../../Utilities/request/request.js';
import { StaticNotification } from '@carbon/react';
import { useTranslation } from '../../../Utilities/i18n/i18n.js';
import uniqueId from '../../../Utilities/id/uniqueId.js';
import Message from '../Message/Message.js';
import Translate from '../Translate/Translate.js';

var _excluded = ["subtitle", "text", "caption", "status", "isTileWrapped", "error", "recovery", "asNotification", "asTooltip"];
var genericError = function genericError(t, id) {
  var additionalProps = {};
  if (id) {
    additionalProps['aria-describedby'] = id;
  }
  return /*#__PURE__*/React.createElement(Translate, {
    tagProps: [_objectSpread2({
      href: '/status',
      target: '_blank'
    }, additionalProps), _objectSpread2({
      href: '/unifiedsupport/supportcenter',
      target: '_blank'
    }, additionalProps)]
  }, t("Try waiting a few minutes and perform the action again to see if the problem persists. If so, check the <a>status page</a> to see if something else is going on. If you're still experiencing an issue reach out to <a>support</a> for help."));
};
var ErrorState = function ErrorState(_ref) {
  var subtitle = _ref.subtitle,
    text = _ref.text,
    caption = _ref.caption,
    _ref$status = _ref.status,
    status = _ref$status === void 0 ? 0 : _ref$status,
    _ref$isTileWrapped = _ref.isTileWrapped,
    isTileWrapped = _ref$isTileWrapped === void 0 ? false : _ref$isTileWrapped,
    error = _ref.error,
    recovery = _ref.recovery,
    asNotification = _ref.asNotification,
    asTooltip = _ref.asTooltip,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(new Date().toISOString().replace(/[-:.]/g, '')),
    _useState2 = _slicedToArray(_useState, 1),
    timestamp = _useState2[0];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isAuthError = _useState4[0],
    setIsAuthError = _useState4[1];
  var _useState5 = useState('ERROR'),
    _useState6 = _slicedToArray(_useState5, 2),
    icon = _useState6[0],
    setIcon = _useState6[1];
  var _useState7 = useState(parseError(error || {})),
    _useState8 = _slicedToArray(_useState7, 2),
    parsedError = _useState8[0],
    setParsedError = _useState8[1];
  var _useState9 = useState(status),
    _useState10 = _slicedToArray(_useState9, 2),
    statusCode = _useState10[0],
    setStatusCode = _useState10[1];
  var _useState11 = useState(timestamp),
    _useState12 = _slicedToArray(_useState11, 2),
    defaultCaption = _useState12[0],
    setDefaultCaption = _useState12[1];
  var incidentId = caption || parsedError.caption || defaultCaption;
  var notificationId = useMemo(function () {
    return uniqueId('error-state');
  }, []);
  var defaultText = useMemo(function () {
    if (isAuthError) {
      return t('Authentication is required or you do not have the correct permissions to perform this action.');
    }
    return genericError(t, asNotification ? notificationId : undefined);
  }, [asNotification, isAuthError, notificationId, t]);
  var messageContent = text || parsedError.text || defaultText;
  var content = useMemo(function () {
    if (subtitle) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, subtitle), " ", /*#__PURE__*/React.createElement("span", null, messageContent));
    }
    return messageContent;
  }, [messageContent, subtitle]);
  useEffect(function () {
    setParsedError(parseError(error || {}));
  }, [error]);
  useEffect(function () {
    setStatusCode(status || parsedError.status);
  }, [status, parsedError]);
  useEffect(function () {
    setIsAuthError(statusCode === 401 || statusCode === 403);
    if (statusCode) setDefaultCaption("".concat(timestamp, "-").concat(statusCode));else setDefaultCaption(timestamp);
  }, [statusCode, timestamp]);
  useEffect(function () {
    if (isAuthError) setIcon('UNAUTHORIZED');else setIcon('ERROR');
  }, [isAuthError]);
  var getAnalyticsProps = useCallback(function () {
    var analyticsProps = {
      'analytics-transactionid': incidentId
    };
    var errorMessage = [statusCode, parsedError === null || parsedError === void 0 ? void 0 : parsedError.code].filter(Boolean).join('-');
    if (errorMessage) {
      analyticsProps['analytics-errormessage'] = errorMessage;
    }
    return analyticsProps;
  }, [incidentId, parsedError === null || parsedError === void 0 ? void 0 : parsedError.code, statusCode]);
  if (asTooltip) {
    return /*#__PURE__*/React.createElement("div", {
      className: "cpc--error-state"
    }, /*#__PURE__*/React.createElement("p", null, content), recovery && /*#__PURE__*/React.createElement("p", {
      className: "cpc--error-state__recovery"
    }, recovery), /*#__PURE__*/React.createElement("p", {
      className: "cpc--error-state__caption"
    }, caption || t('ID: {{incidentId}}', {
      incidentId: incidentId
    })));
  }
  if (asNotification) {
    return /*#__PURE__*/React.createElement("div", {
      className: "cpc--error-state"
    }, /*#__PURE__*/React.createElement(StaticNotification, _extends({
      lowContrast: true,
      kind: "error",
      title: subtitle || t('Error'),
      titleId: notificationId,
      statusIconDescription: t('Error'),
      subtitle: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, messageContent), recovery && /*#__PURE__*/React.createElement("div", {
        className: "cpc--error-state__recovery"
      }, recovery), /*#__PURE__*/React.createElement("div", {
        className: "cpc--error-state__caption"
      }, caption || t('ID: {{incidentId}}', {
        incidentId: incidentId
      })))
      // eslint-disable-next-line react/jsx-props-no-spreading
    }, getAnalyticsProps(), rest)));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "cpc--error-state"
  }, /*#__PURE__*/React.createElement(Message, {
    id: "error-message",
    icon: icon,
    text: recovery ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, content), /*#__PURE__*/React.createElement("div", {
      className: "cpc--error-state__recovery"
    }, recovery)) : content,
    caption: caption || t('ID: {{incidentId}}', {
      incidentId: incidentId
    }),
    tileWrapped: isTileWrapped,
    size: "sm"
  }));
};
ErrorState.propTypes = {
  /**
   * Content to display immediately preceding the `text`. Use this to include an explanation of
   * the action that was taking place that caused the error, and still have the generic error
   * text displayed afterward.
   */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Text to show in the error message (see Message component).
   */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Caption to show in the error message (see Message component).
   */
  caption: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * The error status code. Will override the `status` in the `error` prop if one is provided.
   */
  status: PropTypes.number,
  /**
   * Wrap the error state in a tile (see Message component).
   */
  isTileWrapped: PropTypes.bool,
  /**
   * An error response object. If provided, the status, text, and caption will be obtained by
   * parsing the error using the `parseError()` function from the `request` util. If necessary
   * a string can be provided, although it's preffered to pass an object that has more data.
   */
  error: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  /**
   * Additional helpful content to show after the main content of the error. Use this along
   * with the `error` prop to add content after the message extracted from the error, for things
   * like links to docs or other helpful recovery actions.
   */
  recovery: PropTypes.node,
  /**
   * Render the error as an InlineNotification.
   */
  asNotification: PropTypes.bool,
  /**
   * Render the error as content for displaying within a Tooltip.
   */
  asTooltip: PropTypes.bool
};
var ErrorState$1 = ErrorState;

export { ErrorState$1 as default };
